import { ComboPlateType, PlateGeometryType, PlateOption, PlateType } from '@paragon/contract-library';
import { distanceEquals } from '@paragon/geometry-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { getPlateManufacturer } from './plate-manufacturers';

export function getComboPlateType(plateType: PlateType): ComboPlateType {
  switch (plateType) {
    case 'M20':
    case 'Eagle20':
    case 'TL20':
    case 'MT20':
    case 'AS20':
    case 'AlpineW':
    case 'AlpineT':
    case 'CH20':
    case 'CH20S':
    case 'CherokeeB':
    case 'MII20':
    case 'Any20':
      return 'Combo20';
    case 'M20HS':
    case 'MT20HS':
    case 'Eagle20HS':
    case 'MII20HS':
    case 'AS20HS':
    case 'Any20HS':
    case 'Alpine20H':
      return 'Combo20HS';
    case 'Eagle18':
    case 'TL18':
    case 'MT18':
    case 'AS18S6':
    case 'AS18S5':
    case 'CH18':
    case 'Any18':
      return 'Combo18';
    case 'AS18HS':
    case 'M18SHS':
    case 'MT18HS':
    case 'Eagle18HS':
    case 'Alpine18SS':
    case 'CH18HS':
    case 'M18AHS':
    case 'MTH18':
    case 'SMH18':
      return 'Combo18HS';
    case 'Any18HS':
      return 'Combo18HS';
    case 'M16':
    case 'Eagle16':
    case 'TL16':
    case 'MT16':
    case 'MII16':
      return 'Combo16';
    case 'Combo16':
    case 'Combo18':
    case 'Combo18HS':
    case 'Combo20':
    case 'Combo20HS':
      return plateType;
    default:
      assertExhaustiveSwitch(plateType);
  }
}

export function isComboPlateType(plateType: PlateType): plateType is ComboPlateType {
  return getPlateManufacturer(plateType) === 'Combo';
}

export function getTangiblePlateTypesForComboPlatePair(
  plateType: PlateType,
  width: number,
  length: number,
  geometryType: PlateGeometryType,
  plateOptionsFromPlateList: readonly PlateOption[],
) {
  const matchingComboPlateOptions = plateOptionsFromPlateList.filter(
    (plateOption) =>
      distanceEquals(plateOption.width, width) &&
      distanceEquals(plateOption.length, length) &&
      isComboPlateType(plateOption.plateType) &&
      plateType === plateOption.plateType &&
      geometryType === plateOption.geometryType,
  );
  if (matchingComboPlateOptions.length !== 1) {
    throw new Error('Expected exactly one matching combo plate option, but found ' + matchingComboPlateOptions.length);
  }
  return matchingComboPlateOptions[0].tangiblePlateTypesForComboPlateType;
}

interface PlateTypeWithTangibleTypes {
  readonly plateType: PlateType;
  readonly tangiblePlateTypesForComboPlateType: readonly PlateType[];
}

export function plateTypeWithTangibleTypesEqualityComparator(
  { plateType, tangiblePlateTypesForComboPlateType }: PlateTypeWithTangibleTypes,
  {
    plateType: otherPlateType,
    tangiblePlateTypesForComboPlateType: otherTangiblePlateTypesForComboPlateType,
  }: PlateTypeWithTangibleTypes,
): boolean {
  return (
    plateType === otherPlateType &&
    tangiblePlateTypesForComboPlateType.length === otherTangiblePlateTypesForComboPlateType.length &&
    tangiblePlateTypesForComboPlateType.every((tangiblePlateType) =>
      otherTangiblePlateTypesForComboPlateType.includes(tangiblePlateType),
    ) &&
    otherTangiblePlateTypesForComboPlateType.every((tangiblePlateType) =>
      tangiblePlateTypesForComboPlateType.includes(tangiblePlateType),
    )
  );
}
